'use client';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { MotionButton } from '@/features/motion/components';
import Close from '@/assets/icons/close.svg';
import { PrimaryButton } from '@/components/v2/PrimaryButton';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { div } from 'framer-motion/client';

interface CompetitionModalProps {
    /** Main heading text */
    headingText: string;
    /** Large "WIN" or prize action text */
    actionText?: string;
    /** Prize description */
    prizeDescription: string;
    /** CTA button text */
    ctaText?: string;
    /** Competition page URL */
    competitionUrl: string;
    /** Learn more URL */
    learnMoreUrl: string;
    /** Image source */
    imageSrc: string;
    /** Mobile image source (optional, falls back to imageSrc) */
    mobileImageSrc?: string;
    /** Image alt text */
    imageAlt?: string;
    /** Session storage key (to track if shown) */
    sessionStorageKey?: string;
    /** Delay before showing modal (in ms) */
    showDelay?: number;
}

const ContainerVariants: Variants = {
    hide: { opacity: 0, y: 50 },
    show: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 50 },
};

const CloseButtonVariants: Variants = {
    hide: { scale: 0 },
    show: { scale: 1 },
    exit: { scale: 0 },
};

export function CompetitionModal({ 
    headingText,
    actionText = "WIN",
    prizeDescription,
    ctaText = "Enter Competition Now! 🎉",
    competitionUrl,
    learnMoreUrl,
    imageSrc,
    mobileImageSrc,
    imageAlt = "Competition",
    sessionStorageKey = "competition-modal-shown",
    showDelay = 2000
}: CompetitionModalProps) {
    const [open, setOpen] = useState(false);
    const [modalShown, setModalShown] = useState(false);
    const isDesktop = useMediaQuery('md');

    // Check if modal should show (once per session)
    useEffect(() => {
        // Check if modal has already been shown this session
        const hasShownModal = sessionStorage.getItem(sessionStorageKey);
        
        if (!hasShownModal) {
            // Small delay to ensure page has loaded
            const timer = setTimeout(() => {
                setModalShown(true);
                setOpen(true);
                // Mark as shown for this session
                sessionStorage.setItem(sessionStorageKey, 'true');
            }, showDelay);
            
            return () => clearTimeout(timer);
        }
    }, [sessionStorageKey, showDelay]);

    const handleClose = () => {
        setModalShown(false);
        setOpen(false);
    };

    const handleCompetitionClick = () => {
        handleClose();
        // Navigation will be handled by the Link component
    };

    return (
        <div className="fixed inset-0 z-50">
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent dialogCloseClassName='absolute right-2 top-2' className="border-none overflow-clip py-0 flex w-[calc(100%-2.5rem)] max-w-screen-md flex-col gap-0 rounded-lg px-0 text-center">
                    <div className="grid grid-cols-1 md:grid-cols-2 items-center justify-center">
                        {/* Competition Image */}
                        <div className="w-full h-full">
                            {isDesktop ? (
                                <img 
                                    src={imageSrc} 
                                    alt={imageAlt} 
                                    className="w-full h-full object-fill"
                                />
                            ) : 
                                <img 
                                    src={mobileImageSrc} 
                                    alt={imageAlt} 
                                    className="w-full h-full object-fill block"
                                />
                            }
                        </div>
                        

                        <div className='p-5'>
                            {/* Main Heading */}
                            <h2 className="mb-4 font-bold md:text-[24px] text-[26px] leading-[34px]">
                                {headingText}
                                {actionText && (
                                    <>
                                        <br />
                                        <span className="text-accent-pink text-3xl md:text-4xl">
                                            {actionText}
                                        </span>
                                    </>
                                )}
                            </h2>
                            
                            {/* Prize Description */}
                            <p className="mb-6 max-w-md font-semibold text-gray-800 md:text-[16px] px-[18px] md:px-0">
                                {prizeDescription}
                            </p>

                            <div className="flex flex-col gap-4">
                                {/* CTA Button */}
                                <Link href={competitionUrl} onClick={handleCompetitionClick} className="w-full max-w-sm">
                                    <PrimaryButton
                                            type="submit"
                                            className="relative h-[42px] w-[100%] flex-row items-center justify-center text-sm md:h-[46px] md:w-full md:max-w-[297px]"
                                            href="/collections/all-products"
                                            as={Link}
                                            animateText
                                            align="left">
                                            {ctaText}
                                    </PrimaryButton>
                                </Link>

                                <Link href={learnMoreUrl} className="w-[100%] max-w-sm">
                                    <PrimaryButton
                                            type="submit"
                                            className="relative h-[42px] w-[100%] flex-row items-center justify-center text-sm md:h-[46px] md:w-full md:max-w-[297px]"
                                            href={learnMoreUrl}
                                            as={Link}
                                            animateText
                                            align="left">
                                            Learn More
                                    </PrimaryButton>
                                </Link>

                            </div>

                        </div>
                        
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}